import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Archive, Briefcase, Edit3, ExternalLink, Plus, Search, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });

const STATUS_META = {
  active: { label: "Activo", tone: "active" },
  pending: { label: "Pendiente", tone: "pending" },
  paused: { label: "En pausa", tone: "on_hold" },
  on_hold: { label: "En pausa", tone: "on_hold" },
  completed: { label: "Completado", tone: "completed" },
  archived: { label: "Archivado", tone: "archived" },
};

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "active", label: "Activos" },
  { key: "pending", label: "Pendientes" },
  { key: "completed", label: "Completados" },
  { key: "paused", label: "En pausa" },
];

const emptyForm = () => ({
  step: 1,
  name: "", description: "", client_name: "", category: "", color: "#4169e1",
  status: "active", start_date: "", end_date: "", due_date: "",
  contract_value: "", budget: "", estimated_expenses: "", estimated_revenue: "", currency: "MXN", payment_terms: "",
  collaborators: [],
});

const hexToRgb = (hex) => {
  const clean = (hex || "").replace("#", "");
  if (clean.length !== 6) return "65,105,225";
  const n = parseInt(clean, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};

const lighten = (hex, mix = 0.5) => {
  const rgb = hexToRgb(hex).split(",").map(Number);
  const r = Math.round(rgb[0] + (255 - rgb[0]) * mix);
  const g = Math.round(rgb[1] + (255 - rgb[1]) * mix);
  const b = Math.round(rgb[2] + (255 - rgb[2]) * mix);
  return `rgb(${r},${g},${b})`;
};

const formatDue = (date) => {
  if (!date) return null;
  try {
    const d = new Date(date + "T00:00:00");
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return date; }
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showWizard, setShowWizard] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    const q = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/projects${q}`, { withCredentials: true })
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos cargar los proyectos."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const filtered = useMemo(() => {
    if (filter === "all") return projects;
    return projects.filter((p) => (p.status || "active") === filter);
  }, [projects, filter]);

  const totals = useMemo(() => {
    return projects.reduce((acc, p) => {
      const k = p.kpis || {};
      acc.contract += k.contract_value || 0;
      acc.collected += k.collected || 0;
      acc.expense += k.expense || 0;
      acc.profit += k.profit || 0;
      acc.remaining += k.remaining || 0;
      return acc;
    }, { contract: 0, collected: 0, expense: 0, profit: 0, remaining: 0 });
  }, [projects]);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const { step, collaborators, ...payload } = form;
      const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== ""));
      const r = await api.post("/projects", clean, { withCredentials: true });
      for (const c of collaborators) {
        await api.post(`/projects/${r.data.id}/collaborators`, c, { withCredentials: true });
      }
      setShowWizard(false); setForm(emptyForm());
      navigate(`/proyectos/${r.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos crear el proyecto.");
    } finally { setSaving(false); }
  };

  const archive = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("¿Archivar este proyecto?")) return;
    try {
      await api.post(`/projects/${id}/archive`, {}, { withCredentials: true });
      load();
    } catch (err) { setError(err.response?.data?.detail || "No pudimos archivar."); }
  };

  return (
    <AppShell>
      <main className="page projects-page" data-testid="projects-page">
        <header className="projects-hero">
          <div>
            <small className="eyebrow">PROYECTOS</small>
            <h1>Cada proyecto, <em>su propia oficina.</em></h1>
            <p>Rastrea contrato, cobrado, egresos y utilidad en tiempo real. Sube archivos, escribe notas y observa cada movimiento en el timeline.</p>
          </div>
          <div className="head-actions">
            <button
              className="expenses-add-button"
              onClick={() => { setForm(emptyForm()); setShowWizard(true); }}
              data-testid="new-project-btn"
            ><Plus size={17} /> Nuevo proyecto</button>
          </div>
        </header>

        {projects.length > 0 && (
          <section className="projects-summary" data-testid="projects-summary">
            <div className="card accent-blue"><small>Valor total contratado</small><b data-testid="summary-contract">{money.format(totals.contract)}</b></div>
            <div className="card accent-green"><small>Cobrado</small><b data-testid="summary-collected">{money.format(totals.collected)}</b></div>
            <div className="card accent-orange"><small>Por cobrar</small><b data-testid="summary-remaining">{money.format(totals.remaining)}</b></div>
            <div className="card accent-red"><small>Egresos</small><b data-testid="summary-expense">{money.format(totals.expense)}</b></div>
            <div className="card"><small>Utilidad</small><b style={{ color: totals.profit >= 0 ? "#10b981" : "#ef4444" }} data-testid="summary-profit">{money.format(totals.profit)}</b></div>
          </section>
        )}

        <div className="projects-toolbar">
          <div className="search">
            <Search size={16} color="#94a3b8" />
            <input
              type="text"
              placeholder="Buscar por nombre, cliente o categoría…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="projects-search"
            />
          </div>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`filter-chip ${filter === f.key ? "active" : ""}`}
              data-testid={`filter-${f.key}`}
            >{f.label}</button>
          ))}
        </div>

        {error && <p className="expenses-error" data-testid="projects-error">{error}</p>}

        {loading ? (
          <section className="projects-grid-v2" aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((k) => <div key={k} className="skeleton skeleton-card" />)}
          </section>
        ) : filtered.length === 0 ? (
          <div className="pd-empty" data-testid="projects-empty">
            <div className="pd-empty-icon"><Briefcase size={26} /></div>
            <h3>{projects.length === 0 ? "Aún no tienes proyectos." : "Sin proyectos con ese filtro."}</h3>
            <p>{projects.length === 0 ? "Crea tu primer proyecto para organizar todo su dinero, gente y documentos." : "Prueba con otro filtro o crea uno nuevo."}</p>
            <button type="button" onClick={() => { setForm(emptyForm()); setShowWizard(true); }} className="cash-btn primary" data-testid="projects-empty-add">
              <Plus size={15} /> Crear proyecto
            </button>
          </div>
        ) : (
          <section className="projects-grid-v2" data-testid="projects-grid">
            {filtered.map((p, idx) => {
              const meta = STATUS_META[p.status] || STATUS_META.active;
              const k = p.kpis || {};
              const contract = k.contract_value || 0;
              const collected = k.collected || 0;
              const remaining = k.remaining || 0;
              const pct = k.progress_pct || 0;
              const accent = p.color || "#4169e1";
              const remainingCls = k.remaining_status === "paid" ? "paid" : k.remaining_status === "pending" ? "overdue" : "";
              return (
                <Link
                  to={`/proyectos/${p.id}`}
                  key={p.id}
                  className="project-card-v2"
                  data-testid={`project-card-${p.id}`}
                  style={{ "--accent": accent, "--accent-2": lighten(accent, 0.35), animationDelay: `${Math.min(idx, 6) * 40}ms` }}
                >
                  <div className="head">
                    <div>
                      <div className="name">{p.name}</div>
                      <div className="client">{p.client_name || "Sin cliente"}{p.category ? ` · ${p.category}` : ""}</div>
                    </div>
                    <span className={`status-badge ${meta.tone}`}>{meta.label}</span>
                  </div>

                  <div className="progress-block">
                    <div className="progress-label">
                      <span>Avance de cobro</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
                  </div>

                  <div className="financials">
                    <div className="fin"><em>Contrato</em><b>{money.format(contract)}</b></div>
                    <div className="fin"><em>Cobrado</em><b style={{ color: "#10b981" }}>{money.format(collected)}</b></div>
                    <div className={`fin remaining ${remainingCls}`}><em>Por cobrar</em><b>{money.format(remaining)}</b></div>
                    <div className="fin"><em>Utilidad</em><b style={{ color: (k.profit || 0) >= 0 ? "#10b981" : "#ef4444" }}>{money.format(k.profit || 0)}</b></div>
                  </div>

                  <div className="card-foot">
                    <span className="due-date">{p.due_date || p.end_date ? `Vence ${formatDue(p.due_date || p.end_date)}` : `${(p.collaborators?.length || 0)} colaborador(es)`}</span>
                    <div className="quick-actions">
                      <span title="Abrir" aria-label="Abrir"><ExternalLink size={13} /></span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/proyectos/${p.id}?tab=settings`); }}
                        title="Editar"
                        data-testid={`edit-project-${p.id}`}
                      ><Edit3 size={13} /></button>
                      {p.status !== "archived" && (
                        <button
                          type="button"
                          onClick={(e) => archive(e, p.id)}
                          title="Archivar"
                          data-testid={`archive-project-${p.id}`}
                        ><Archive size={13} /></button>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </section>
        )}

        {showWizard && (
          <ProjectWizard form={form} setForm={setForm} saving={saving} onCancel={() => setShowWizard(false)} onSubmit={submit} error={error} />
        )}
      </main>
    </AppShell>
  );
}

function ProjectWizard({ form, setForm, saving, onCancel, onSubmit, error }) {
  const step = form.step;
  const next = () => setForm({ ...form, step: Math.min(3, step + 1) });
  const back = () => setForm({ ...form, step: Math.max(1, step - 1) });
  const addCollab = () => setForm({ ...form, collaborators: [...form.collaborators, { email: "", role: "collaborator" }] });
  const setCollab = (i, patch) => {
    const copy = [...form.collaborators];
    copy[i] = { ...copy[i], ...patch };
    setForm({ ...form, collaborators: copy });
  };

  return (
    <div className="cash-modal-backdrop" onClick={onCancel} data-testid="project-wizard">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(600px, 96vw)" }}>
        <header>
          <div>
            <small>NUEVO PROYECTO · PASO {step} DE 3</small>
            <b>{step === 1 ? "Información general" : step === 2 ? "Contrato y presupuesto" : "Colaboradores"}</b>
          </div>
          <button type="button" onClick={onCancel} aria-label="Cerrar"><X size={18} /></button>
        </header>
        <div style={{ padding: "0 24px 20px" }}>
          <div className="tour-progress-bar" style={{ marginBottom: 16 }}><div style={{ width: `${(step / 3) * 100}%` }} /></div>

          {step === 1 && (
            <div className="cash-modal-grid">
              <label className="full">Nombre del proyecto *<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Rediseño web ACME" data-testid="project-name" /></label>
              <label>Cliente<input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Ej: ACME S.A. de C.V." data-testid="project-client" /></label>
              <label>Categoría<input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej: Diseño, Construcción, Consultoría" data-testid="project-category" /></label>
              <label className="full">Descripción<textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Breve descripción del alcance del proyecto…" /></label>
              <label>Color<input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="project-color" /></label>
              <label>Estatus
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="project-status">
                  <option value="active">Activo</option>
                  <option value="pending">Pendiente</option>
                  <option value="paused">En pausa</option>
                  <option value="completed">Completado</option>
                </select>
              </label>
              <label>Fecha de inicio<input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
              <label>Vencimiento<input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="project-due-date" /></label>
            </div>
          )}
          {step === 2 && (
            <div className="cash-modal-grid">
              <label className="full">Valor del contrato (lo que cobrarás)<input type="number" step="0.01" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} placeholder="Ej: 100000" data-testid="project-contract-value" /></label>
              <label className="full">Presupuesto interno (costos)<input type="number" step="0.01" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="Ej: 40000" data-testid="project-budget" /></label>
              <label className="full">Términos de pago<input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Ej: 50% anticipo, 50% a la entrega" /></label>
            </div>
          )}
          {step === 3 && (
            <>
              <p style={{ color: "#64748b", fontSize: 13 }}>Invita gente por correo. Si ya tienen cuenta en Mi Contador, ven el proyecto de inmediato. Si no, reciben invitación.</p>
              {form.collaborators.map((c, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginBottom: 8 }} data-testid={`project-collab-${i}`}>
                  <label>Correo<input placeholder="correo@ejemplo.com" value={c.email} onChange={(e) => setCollab(i, { email: e.target.value })} data-testid={`project-collab-email-${i}`} /></label>
                  <label>Rol<select value={c.role} onChange={(e) => setCollab(i, { role: e.target.value })}>
                    <option value="admin">Admin</option>
                    <option value="collaborator">Colaborador</option>
                    <option value="viewer">Lectura</option>
                  </select></label>
                </div>
              ))}
              <button type="button" onClick={addCollab} className="cash-btn ghost" data-testid="project-add-collab">+ Agregar colaborador</button>
            </>
          )}
          {error && <p className="expenses-error" style={{ marginTop: 12 }}>{error}</p>}
        </div>
        <div className="cash-modal-actions">
          {step > 1 && <button type="button" onClick={back} className="cash-btn ghost">Atrás</button>}
          {step < 3 ? (
            <button type="button" onClick={next} className="cash-btn primary" disabled={step === 1 && !form.name} data-testid="project-wizard-next">Siguiente</button>
          ) : (
            <button type="button" onClick={onSubmit} className="cash-btn primary" disabled={saving || !form.name} data-testid="project-wizard-submit">
              {saving ? "Creando..." : "Crear proyecto"} <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
